import * as _ from 'lodash'
import { ExecuteContext, HandlerObject, KV, OperateFunction, Operator, PlanContext, PluginDefinition, PluginInfoList } from 'yam-types'
import * as BASE_SCHEMA from '../schema/base_schema.json'
import * as SETTINGS_SCHEMA from '../schema/settings_schema.json'
import TemplateRender from '../template/render'
import type { OperatorParam, YamPlugin } from '../types'
import { createLogger, sha256, splitAndTrim } from '../util'

/**
 * YamPluginComposer handles plugin modules, it will:
 * a. analyze plugin JSON Schema, merge into one schema
 * b. analyze JSON path handler functions defined in each plugin
 */
export default class YamPluginComposer {

  builtInPlugins: PluginDefinition<unknown>[] = []

  private templateRender?: TemplateRender
  private log = createLogger('plugin-handler')

  constructor(templateRender?: TemplateRender) {
    this.templateRender = templateRender
  }

  /**
   * Schema builder will import plugin codes, retrieve JSON Schema and Handler Functions,
   * Inside a plugin, each JSON Schema defines subset of final YAM schema,
   * while each JSON-Path matcher and OperateFunction of the plugin handler define operation logic.
   * 
   * An example plugin should provide default exported object like following:
   * ```js
   * {
   *   "name": "example-plugin",
   *   "schema": {
   *     "$schema": "http://json-schema.org/draft-07/schema",
   *     "type": "object"
   *     "properties": {}
   *   },
   *   "handlers": {
   *      "access.route": 
   *      "config[?(@.type=='configMap')]": async (plan, param, diff) => {}
   *   }
   * }
   * ```
   * NOTE: YAM engine will add "$." prefix if it's not defined in Matcher JSON Path Expression
   * 
   * @see https://json-schema.org/specification.html
   * @see https://tools.ietf.org/id/draft-goessner-dispatch-jsonpath-00.html
   */
  public async compose(plugins: YamPlugin[], param?: OperatorParam, disabledPlugins?: string[], customizedSettings?: KV, customizedValues?: KV): Promise<SchemaHandlerFunc> {
    const uninstallMode = param?.runMode === "uninstall"
    const filteredPlugins = param?.filteredPlugins

    const settingsSchema = JSON.parse(JSON.stringify(SETTINGS_SCHEMA))
    // To remove default settings for strict IDE validation : settingsSchema.properties.plugins.items.anyOf.pop()
    let mergedJsonSchema: unknown = BASE_SCHEMA

    const mergedHandlers: MergedHandler[] = []
    const lifecycleHandlers: LifecycleHandlers = {
      beforePlan: [],
      afterPlan: [],
      beforeApply: [],
      afterApply: []
    }
    const pluginInfoList: PluginInfoList = {}
    this.log.info(`start composing plugin handlers...`)

    for (const plugin of plugins) {
      if (!plugin.enable) {
        this.log.debug(`plugin disabled because of global level settings`)
        continue
      }

      if (disabledPlugins && disabledPlugins.length > 0 && disabledPlugins.includes(plugin.name)) {
        this.log.debug(`plugin disabled because of env level settings`)
        continue
      }

      // when plugin filters are specified, only selected/matched plugins are enabled
      if (filteredPlugins && filteredPlugins.length > 0) {
        if (filteredPlugins.filter(p => {
          const tmp = splitAndTrim(plugin.name)
          return tmp[tmp.length - 1] === p
        }).length === 0) {
          this.log.debug(`skipped plugin: ${plugin.name} because of command line parameters`)
          continue
        }
      }

      // import plugin as commonjs module dynamically
      let requiredPlugin
      try {
        // dynamic import plugin codes in plugin directory
        if (!plugin.builtIn) {
          if (!plugin.directory) {
            throw new Error(`plugin directory not provided for ${plugin.name}`)
          }
          this.log.info(`load external plugin: ${plugin.name}:${plugin.version}`)
          // use eval function to avoid webpack_require replacing
          requiredPlugin = eval(`require('${plugin.directory.replace(/\\/g, '\\\\')}');`)
        } else {
          this.log.info(`load built-in plugin: ${plugin.name}:${plugin.version}`)
          const builtInPlugin = this.builtInPlugins.find(p => p.name === plugin.name)
          if (!builtInPlugin) {
            throw new Error(`built-in plugin ${plugin.name} is not found, please check YAM engine settings.`)
          }
          requiredPlugin = { default: builtInPlugin }
        }
      } catch (err) {
        this.log.error(err)
        throw new Error(`import plugin ${plugin.name}:${plugin.version} in dir '${plugin.directory || ''}' failed`)
      }
      const pluginModule = requiredPlugin.default as PluginDefinition<unknown>

      // schema is the json schema for validating currentYam object, merge all plugins schema
      mergedJsonSchema = this.mergeJsonSchema(pluginModule.schema, mergedJsonSchema)

      // merge to settings schema for settings auto completion
      const def = settingsSchema.$definitions
      const currentPluginSettingJsonStr = JSON.stringify(pluginModule.settings || {
        type: "object",
        additionalProperties: true,
      })
      const currentPluginSetting = JSON.parse(currentPluginSettingJsonStr)
      delete currentPluginSetting.$schema
      currentPluginSetting.default = {}
      def.pluginSettings.properties[pluginModule.name] = currentPluginSetting
      const copiedPlugin = JSON.parse(JSON.stringify(settingsSchema.$definitions.plugin))
      copiedPlugin.properties.name = { const: pluginModule.name }
      const tmp = pluginModule.name.split('/')
      copiedPlugin.title = `Plugin Settings ${tmp[tmp.length - 1]}`
      copiedPlugin.description = `Settings for ${pluginModule.name} of version ${pluginModule.version}`
      copiedPlugin.properties.settings = currentPluginSetting
      settingsSchema.properties.plugins.items.anyOf.push(copiedPlugin)

      pluginInfoList[pluginModule.name] = {
        version: pluginModule.version,
        settingHash: sha256(currentPluginSettingJsonStr)
      }

      // merge all plugins handlers, build handler array
      if (pluginModule.name !== plugin.name) {
        throw new Error(`invalid plugin definition, plugin ${plugin.name}'s module name ${pluginModule.name} mis-match`)
      }
      // make sure settings object is not null/undefined
      const envSettings = (customizedSettings || {})[plugin.name] || {}
      const mergedSettings = JSON.parse(JSON.stringify(Object.assign({}, plugin.settings, envSettings)))
      this.templateRender?.walkObject(mergedSettings, customizedValues || {})
      await this.mergeHandlers(pluginModule, mergedHandlers, mergedSettings, uninstallMode, lifecycleHandlers)
    }

    this.sortHandlers(mergedHandlers)
    this.log.info(`plugins and schema loaded, ${mergedHandlers.length} operation handlers composed.`)
    return { 
      jsonSchema: mergedJsonSchema,
      settingsSchema, 
      handlers: mergedHandlers,
      lifecycleHandlers,
      pluginInfoList
    }
  }

  /**
   * resolve operator functions of certain plugin
   */
  public async mergeHandlers(
    pluginModule: PluginDefinition<unknown>, mergedHandlers: MergedHandler[],
    settings: KV, uninstallMode?: boolean, lifecycleHandlers?: LifecycleHandlers
  ): Promise<void> {
    const tmpHandlers = pluginModule.handlers
    const handlers = (tmpHandlers instanceof Promise) ? (await tmpHandlers) : tmpHandlers
    const name = pluginModule.name
    for (const jsonPathMatcher in handlers) {
      let handlerFunc = handlers[jsonPathMatcher]
      if (lifecycleHandlers && Object.keys(lifecycleHandlers).includes(jsonPathMatcher)) {
        ((lifecycleHandlers as KV)[jsonPathMatcher] as Array<unknown>)?.push(this.wrapSettingsProvider(name, settings, handlerFunc, jsonPathMatcher))
        continue
      }
      const handlerObj = handlerFunc as HandlerObject<unknown>
      // merge uninstallers
      if (uninstallMode) {
        if (handlerObj.uninstall) {
          handlerFunc = handlerObj.uninstall
        } else {
          continue
        }
      } else if (handlerObj.install) {
        handlerFunc = handlerObj.install
      }
      if (handlerFunc instanceof Function) {
        // use Class style operator declaration
        if (handlerFunc.prototype && handlerFunc.prototype.operate) {
          this.log.debug(`find operator class of ${name}: ${handlerFunc.name}`)
          handlerFunc = YamPluginComposer.wrapClassStyleOperator(handlerFunc as ObjectConstructor)
        } else {
          // arrow function style operator
          this.log.debug(`find operator function of ${name}: ${handlerFunc.name}()`)
        }

        // isolate running environment vars to avoid key disclosure, and log exceptions if fails
        const wrappedFunc = this.wrapSettingsProvider(pluginModule.name, settings, handlerFunc, jsonPathMatcher)

        // merge the function and plugin schema into mergedHandlers
        const res = mergedHandlers.find((h) => { h.matcher === jsonPathMatcher })
        const finalHandler = { pluginName: name, func: wrappedFunc }
        if (res) {
          res.handlers.push(finalHandler)
        } else {
          mergedHandlers.push({ matcher: jsonPathMatcher, handlers: [finalHandler] })
        }
      } else {
        this.log.warn(`wrong handler definition for schema path ${jsonPathMatcher} of plugin ${name}`)
      }
    }
  }

  /**
   * sort handlers by it's matcher expression, to make it run in correct order
   */
  private sortHandlers(mergedHandlers: MergedHandler[]) {
    // order of workflow stages
    const order = {
      metadata: 1e1,
      prepare: 1e2,
      config: 1e3,
      deploy: 1e4,
      access: 1e5,
      observe: 1e6,
      scale: 1e7,
    } as { [key: string]: number }
    const getFirstPropOrder = (str: string): number => {
      if (str.startsWith("$.")) {
        str = str.substring(2)
      }
      if (str.indexOf(".") !== -1) {
        str = str.substring(0, str.indexOf("."))
      }
      if (str.indexOf("[") !== -1) {
        str = str.substring(0, str.indexOf("["))
      }
      return order[str] || Number.MAX_SAFE_INTEGER
    }
    mergedHandlers.sort((h1, h2) => {
      const m1 = getFirstPropOrder(h1.matcher)
      const m2 = getFirstPropOrder(h2.matcher)
      if (m1 == m2) {
        // run in same stage, compare matcher expression length, the shortest one run at first
        return h1.matcher.length - h2.matcher.length > 0 ? 1 : -1
      } else {
        return m1 - m2 > 0 ? 1 : -1
      }
    })
  }


  /**
   * resolve json schema, merge into one final schema
   */
  private mergeJsonSchema(sourceSchema: unknown, mergedSchema: unknown) {
    return _.mergeWith({}, sourceSchema, mergedSchema, (mergedValue, newValue, key) => {
      if (_.isNil(mergedValue)) {
        return
      }
      if (key === 'anyOf') {
        return mergedValue.concat(newValue)
      }
      if (key === 'required') {
        return _.uniq(mergedValue.concat(newValue))
      }
      if (_.isPlainObject(mergedValue)) {
        if (!_.isPlainObject(newValue)) {
          throw new Error(`Failed to merge schemas because "${key}" has different values.`)
        }
        return
      }
      return
    })
  }

  /**
   * Wrap environment variables of each plugin into closure
   */
  private wrapSettingsProvider(pluginName: string, settings: KV, handlerFunc: unknown, jsonPath: string): OperateFunction<unknown> {
    if (!settings) {
      this.log.warn(`settings of plugin '${pluginName}' not specified`)
      settings = {}
    }
    // handle placeholder like ${VAR_FROM_ENV}
    for (const key in settings) {
      const val = settings[key]
      if (typeof val === "string" && val.startsWith('${') && val.endsWith('}')) {
        const envKey = val.substring(2, val.length - 1).trim()
        this.log.debug(`replace settings value with environment variable '${envKey}'`)
        settings[key] = process.env[envKey] || ''
      }
    }
    return async (...args: unknown[]) => {
      try {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await (handlerFunc as OperateFunction<unknown>).apply({}, [...args, settings])
      } catch (ex) {
        (args[0] as PlanContext).log.error(`plugin ${pluginName} failed during plan, caused by '${jsonPath}'`, ex)
        throw ex
      }
    }
  }

  /**
   * class that implement 'operate' interface, create a instance and then wrap the function
   */
  public static wrapClassStyleOperator(handlerFuncClass: ObjectConstructor): OperateFunction<unknown> {
    const operateFunc = (handlerFuncClass.prototype as Operator<unknown>).operate
    return (() => {
      const instance = new handlerFuncClass()
      return async (...args: unknown[]) => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        await operateFunc.apply(instance, args)
      }
    })()
  }
}

export type SchemaHandlerFunc = {
  jsonSchema: unknown,
  handlers: MergedHandler[],
  settingsSchema: unknown,

  lifecycleHandlers?: LifecycleHandlers,
  pluginInfoList: PluginInfoList
}

export type MergedHandler = {
  matcher: string,
  handlers: { pluginName: string, func: OperateFunction<unknown> }[]
}

export type ExtractMergedHandlerOpFnDiffType<MH> = 
    MH extends {handlers: infer H} 
    ? H extends { func: infer OP}[]
    ? OP extends OperateFunction<infer T>
    ? T
    : never
    : never
    : never

export type LifecycleHandlers = {
  beforePlan: LifecyclePlanContext[],
  afterPlan: LifecyclePlanContext[],
  beforeApply: LifecycleApplyContext[]
  afterApply: LifecycleApplyContext[]
}

export type LifecyclePlanContext = (ctx: PlanContext, diff: null, settings: KV) => Promise<void>

export type LifecycleApplyContext = (ctx: ExecuteContext, diff: null, settings: KV) => Promise<void>